/**
 * INVOICE OVERRIDES — every charge somebody decided not to bill, and what it cost.
 *
 * Invoice Reconciliation answers "what are we about to under-bill". This answers the
 * question underneath it: of the charges we are knowingly not billing, who decided, on
 * what grounds, and how much. The two are deliberately separate reports because they read
 * different sources — Reconciliation reads the estimate cache, which holds OPEN orders
 * only, and 217 of the 257 overrides in test are on orders that have already invoiced.
 * Those are exactly the rows this report exists for, so it reads the override table.
 *
 * WHAT MAKES THIS PAGE HONEST, in the order the reader meets it:
 *
 *  1. THE AMOUNT IS A NET, AND ITS SIGN IS A FINDING. What the charge is worth minus
 *     what the invoice already bills for it — the same number on the ordinary override
 *     where nothing was billed, and smaller where the charge was partly billed already.
 *     A NEGATIVE means the override preserved a charge the quote does not support: we
 *     are billing more than we can justify, the opposite problem from the one this
 *     report is named after, and it renders purple to say so. Summing the gross instead
 *     overstated prod by 16.8% and hid those rows completely.
 *
 *     Provenance is not shown. `amount_source` records whether a figure was stamped at
 *     the decision, filled by the sweep, or reconstructed by
 *     scripts/backfill_override_amounts — kept for audit, deliberately off the page
 *     (Lisa, 2026-09-28), because a marker on every row invites the reader to discount
 *     some of them when the report's job is to give one number.
 *
 *     NULL is still not zero. Where the engine proposes no line at all there is no
 *     amount, and those render as a dash and stay out of the totals rather than being
 *     counted as nothing.
 *
 *  2. THE FILTER BAR HOLDS ONLY WHAT THE TABLE CANNOT. Date and LC change which rows
 *     the server fetches; charge, reason and who-overrode-it are columns already on
 *     the row, so the grid filters them with no round trip. Offering both would have
 *     been two ways to ask one question, and the cards — which always describe the
 *     server's result — would then disagree with the grid depending which you used.
 *
 *     The reason text is messy (252 of 257 rows are free text predating the dropdown,
 *     one intent spelled five ways) but the page does not METER that: it is
 *     self-correcting as the dropdown takes over, and a number whose only destination
 *     is zero is not a measure. Raw text is shown exactly as stored — no typo table is
 *     invented anywhere.
 *
 *  3. THE DATE YOU ASK ON CHANGES THE ANSWER, so the basis is an explicit control rather
 *     than an assumption: override date for "what did the team write off in September",
 *     invoice date to tie to a billing period, delivery date to tie to when the work
 *     happened. Rows with no date on the chosen basis drop out of a filtered range.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect from '../../components/SingleSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import Tile from '../../components/reports/ReportTile';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import invoiceOverridesService, {
  OverrideDateBasis,
  OverrideFilterOptions,
  InvoiceOverrideRow,
  InvoiceOverridesResponse,
} from '../../services/invoiceOverrides.service';

const DATE_BASIS_OPTIONS: { value: OverrideDateBasis; label: string }[] = [
  { value: 'override', label: 'Override date' },
  { value: 'invoice', label: 'Invoice date' },
  { value: 'delivery', label: 'Delivery date' },
];

const DATE_BASIS_HELP: Record<OverrideDateBasis, string> = {
  override: 'When the decision was made. Use this to see what the team wrote off in a period.',
  invoice: 'The date on the order\'s invoice. Use this to tie overrides to a billing period. '
         + 'Orders not yet invoiced have no invoice date and drop out of a filtered range.',
  delivery: 'The delivery leg\'s arrival — when the work happened. Containers not yet '
          + 'delivered drop out of a filtered range.',
};

const money = (v: number) => {
  const body = Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  // Sign before the symbol. `$-740` is not how anyone writes money.
  return `${v < 0 ? '-' : ''}$${body}`;
};

const num = (v: number | null | undefined) => Number(v ?? 0).toLocaleString();

/** Local calendar date as YYYY-MM-DD. NOT toISOString(): that converts to UTC first, so
 *  a date picked west of UTC comes out as the previous day and quietly shifts the window. */
const ymd = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** A stored datetime as a plain date. The column is a string from the API; slicing beats
 *  parsing, which would re-introduce the timezone shift `ymd` exists to avoid. */
const shortDate = (v: string | null) => (v ? String(v).slice(0, 10) : '');

/** Midnight today, and midnight 30 days back — the default window.
 *
 *  Computed at mount rather than as a module constant: a tab left open across midnight
 *  would otherwise keep filtering to the day it was opened on. */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const daysAgo = (n: number) => {
  const d = startOfToday();
  d.setDate(d.getDate() - n);
  return d;
};

/** One key for the grid's settings AND the saved extra-field selection — the hook
 *  namespaces the latter as `additional_fields_<key>`. */
const SETTINGS_KEY = 'invoice-overrides';

function InvoiceOverridesContent() {
  // Override date, last 30 days. The decision date is the one the report is usually
  // asked about ("what did we write off this month"), and it is the only basis every row
  // is guaranteed to have — an order that has not invoiced or delivered yet would drop
  // out of a bounded range on either of the others.
  //
  // Both ends stay clearable, and clear/clear is still every override ever recorded. The
  // window is a starting point, not a floor: this report's whole subject is rows that
  // outlive their order, so the full history has to remain one click away.
  const [basis, setBasis] = useState<OverrideDateBasis>('override');
  const [dateFrom, setDateFrom] = useState<Date | null>(() => daysAgo(30));
  const [dateTo, setDateTo] = useState<Date | null>(() => startOfToday());
  const [lc, setLc] = useState<string[]>([]);

  // The column editor's extra order fields. Split deliberately: the SELECTION is
  // needed by `load` below, while the generated COLUMNS need the column defs that are
  // built further down — which is why the hook comes in two halves.
  //
  // This also owns PERSISTENCE, the piece my first attempt missed. TanTable's settings
  // blob excludes the requested field set on purpose ("owned by the host"), so an
  // added column came back after a reload with nothing behind it.
  const {
    additionalFields,
    onAdditionalFieldsChange,
    ready: fieldsReady,
  } = useAdditionalFieldSelection(SETTINGS_KEY);

  const [data, setData] = useState<InvoiceOverridesResponse | null>(null);
  const [options, setOptions] = useState<OverrideFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  useEffect(() => {
    invoiceOverridesService.filterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    invoiceOverridesService.list({
      dateBasis: basis,
      from: ymd(dateFrom),
      to: ymd(dateTo),
      lc: lc.length ? lc.join(',') : undefined,
      fields: additionalFields.length ? additionalFields.join(',') : undefined,
    })
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [basis, dateFrom, dateTo, lc, additionalFields]);

  // Held on `ready`: until the saved column selection is known, loading would fetch
  // once without the user's extra fields and again with them.
  useEffect(() => { if (fieldsReady) load(); }, [load, fieldsReady]);

  const rows = data?.rows ?? [];

  const lcOptions = useMemo<MultiSelectOption[]>(
    () => (options?.lcs ?? []).map((o) => ({ value: o.value, label: o.label })),
    [options]);

  const totals = data?.totals;
  const hasFilters = !!(dateFrom || dateTo || lc.length);

  // Clear means ALL of it, including the default 30-day window — the full override
  // history is the thing a cleared filter bar should show on a report about rows that
  // outlive their order.
  const clearAll = () => { setDateFrom(null); setDateTo(null); setLc([]); };

  const columns = useMemo<ColumnDef<InvoiceOverrideRow>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO',
      cell: ({ getValue }) => {
        const pro = getValue() as number;
        return (
          <button
            type="button"
            onClick={() => setSelectedPro(pro)}
            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
          >
            {pro}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          </button>
        );
      },
    },
    { accessorKey: 'customer', header: 'Customer' },
    { accessorKey: 'billto', header: 'Bill To' },
    { accessorKey: 'lc_name', header: 'LC' },
    { accessorKey: 'line_item', header: 'Charge' },
    {
      accessorKey: 'reason',
      header: 'Reason',
      // Shown exactly as stored, typos included. Normalising here would make the report
      // look tidier than the data is, and the untidiness is itself a finding.
      cell: ({ getValue }) => {
        const v = (getValue() as string) || '';
        if (!v) return <span className="text-gray-300 dark:text-gray-600">—</span>;
        return <span className="block max-w-[22rem] truncate" title={v}>{v}</span>;
      },
    },
    {
      accessorKey: 'comments',
      header: 'Additional Comments',
      cell: ({ getValue }) => {
        const v = (getValue() as string) || '';
        if (!v) return <span className="text-gray-300 dark:text-gray-600">—</span>;
        return <span className="block max-w-[26rem] truncate" title={v}>{v}</span>;
      },
    },
    { accessorKey: 'overridden_by_name', header: 'Overridden By' },
    {
      accessorKey: 'overridden_at',
      header: 'Override Date',
      cell: ({ getValue }) => shortDate(getValue() as string | null),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      // `meta.aggType` rather than letting TanTable sniff the rendered text: an
      // unrecorded amount renders as an em dash, and a column whose first rows are dashes
      // would not be detected as money at all — losing the footer total silently.
      meta: { aggType: 'money', aggDefault: 'sum' },
      // An amount is an amount. Where it came from — stamped at the decision, filled by
      // the sweep, or reconstructed by the backfill — is recorded in `amount_source` for
      // audit, and deliberately NOT surfaced here (Lisa, 2026-09-28): a provenance marker
      // on every row invites the reader to discount some of them, when the report's job
      // is to give one number for what we declined to bill.
      cell: ({ row, getValue }) => {
        // A blank is still a blank — not $0, just unknown — so it must not render as a
        // figure and must not be summed.
        if (!row.original.amount_known) {
          return <span className="text-gray-300 dark:text-gray-600">—</span>;
        }
        const v = Number(getValue() ?? 0);
        // A NEGATIVE is the opposite finding and must not read as more of the same. The
        // override preserved a charge the quote does not support, so we are billing the
        // customer MORE than we can justify — a customer-facing error, where the rest of
        // this column is revenue we chose to forgo. Purple, matching "Billed, not owed"
        // on Invoice Reconciliation, so the two reports name it the same way.
        const cls = v < 0
          ? 'text-purple-700 dark:text-purple-300'
          : 'text-red-600 dark:text-red-400';
        const r = row.original;
        return (
          <span
            className={`font-mono ${cls}`}
            title={v < 0
              ? `This PRO bills more for ${r.line_item} than the quote supports `
                + `($${Number(r.gross_amount ?? 0).toLocaleString()}). The override `
                + 'kept the higher figure.'
              : undefined}
          >
            {money(v)}
          </span>
        );
      },
    },
    {
      // No separate Invoiced flag: the date IS the flag. A blank invoice date means the
      // order has not been invoiced, so a badge column beside it only restated what the
      // date already showed — and the Already invoiced card carries the count.
      accessorKey: 'invoice_date',
      header: 'Invoice Date',
      cell: ({ getValue }) => shortDate(getValue() as string | null),
    },
    {
      accessorKey: 'delivery_date',
      header: 'Delivery Date',
      cell: ({ getValue }) => shortDate(getValue() as string | null),
    },
    { accessorKey: 'movetype', header: 'Move Type' },
  ], []);

  const { columns: columnsWithExtras, availableFields } =
    useAdditionalFieldColumns<InvoiceOverrideRow>(
      { additionalFields, onAdditionalFieldsChange, ready: fieldsReady },
      { baseColumns: columns },
    );

  return (
    <div className="p-4 space-y-3 h-full flex flex-col">
      <div className="shrink-0 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Invoice Overrides
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 max-w-3xl">
            Every charge someone decided not to bill — who decided, on what grounds, and
            what it was worth. Includes orders that have already invoiced, which is most
            of them.
          </p>
        </div>
        {totals && (
          <div className="text-right text-xs text-gray-500 dark:text-gray-400">
            <div>{num(totals.overrides)} overrides · {num(totals.pros)} PROs</div>
          </div>
        )}
      </div>

      <FilterBar wrap label="Filters">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Date
          </span>
          <SingleSelect
            options={DATE_BASIS_OPTIONS}
            value={basis}
            onChange={(v) => setBasis(v as OverrideDateBasis)}
            size="sm"
            className="w-36"
            usePortal
          />
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={dateFrom}
            onChange={(val) => setDateFrom((val as Date | null) ?? null)}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="From"
          />
          <span className="text-xs text-gray-400">to</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={dateTo}
            onChange={(val) => setDateTo((val as Date | null) ?? null)}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="To"
          />
        </div>
        <FilterBar.Divider />
        <MultiSelect
          options={lcOptions}
          value={lc}
          onChange={setLc}
          placeholder="All LCs"
          showClearOption
          clearOptionLabel="All LCs"
          enableSearch
          searchPlaceholder="Search LCs…"
          preserveOrder
          size="sm"
        />
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Clear filters
          </button>
        )}
      </FilterBar>

      {totals && (
        <div className="shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* Named for the thing it sums — the override's amount — rather than for what
              the override did to it. The footnote carries no denominator: after the
              backfill "with an amount" is all but every row, and a caveat that is almost
              always vacuous teaches people to skip the line it sits on. */}
          <Tile
            label="Override Amount"
            value={money(Number(totals.amount_total ?? 0))}
            hero
            tone="red"
            footnote={`across ${num(totals.overrides)} override${totals.overrides === 1 ? '' : 's'}`}
            help={'What the declined charges were worth — the revenue these overrides '
                + 'are holding off the invoice.'}
          />
          {/* PROs lead, overrides follow: the question is how many orders we are writing
              charges off on, and one order routinely carries several overrides. */}
          <Tile
            label="PROs"
            value={num(totals.pros)}
            tone="slate"
            footnote={`${num(totals.overrides)} override${totals.overrides === 1 ? '' : 's'}`}
            help={'Orders carrying at least one override in range.\n\nFewer than the '
                + 'override count, because one order can have several charges declined.'}
          />
          {/* The same money as the hero card, split by whether the decision can still
              be reversed. Not-yet-invoiced LEADS because it is the half somebody can
              still act on; the invoiced figure is the cost of the ones nobody can. A
              count of overrides said neither thing. */}
          <Tile
            label="Not Yet Invoiced"
            value={money(Number(totals.amount_open ?? 0))}
            tone="blue"
            footnote={`${money(Number(totals.amount_invoiced ?? 0))} already invoiced`}
            help={'Of the override amount, how much sits on orders that have NOT been '
                + 'invoiced yet — still changeable, so still recoverable if a charge '
                + 'was declined in error.\n\nThe rest is on invoiced orders, where the '
                + 'decision is final.'}
          />
        </div>
      )}

      {data?.truncated && (
        <p className="shrink-0 text-xs text-amber-700 dark:text-amber-400">
          Showing the first {num(rows.length)} rows. Narrow the date range to see the rest.
        </p>
      )}

      <div className="flex-1 min-h-0">
        <TanTable
          data={rows}
          columns={columnsWithExtras}
          loading={loading}
          errorMessage={error}
          onRetry={load}
          settingsKey={SETTINGS_KEY}
          searchPlaceholder="Search overrides…"
          enableColumnFilters
          enableGrouping
          trackViewedColumnId="pronumber"
          availableFields={availableFields}
          additionalFields={additionalFields}
          onAdditionalFieldsChange={onAdditionalFieldsChange}
          fillContainer
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
        // Straight to Invoicing: the override lives on that tab, so anyone clicking a PRO
        // here is going there. Alysse asked for the same on Invoice Reconciliation.
        initialTab="invoicing"
      />
    </div>
  );
}

function InvoiceOverrides() {
  return (
    <ViewedProsProvider>
      <InvoiceOverridesContent />
    </ViewedProsProvider>
  );
}

export default InvoiceOverrides;
